import { type QueryClient } from '@tanstack/react-query';
import {
    type WorkQueueMeta,
    type StatusMeta,
    type StepTask,
    type WorkQueueCase
} from '../../services/workQueue';
import { formatPracticeDoctorLabel } from '../shared/practiceDoctorLabelFormat';
import { reportError } from '../../services/errorReporting';
import { invalidateClarificationHistory } from './tasks/useClarificationHistory';
import { invalidateSidebarCounts } from '../../hooks/useSidebarCounts';
import { DX_REPORT_DRAFTS_KEY, FINAL_REVIEW_HAND_BACK_KEY } from '../../hooks/queryKeys';
import {
    FINAL_REVIEW_STATUS_ID,
    FINAL_REVIEW_TASK_ID,
    PENDING_APPROVAL_STATUS_ID,
    APPROVAL_REVIEW_TASK_ID,
    SDI_REVIEW_STATUS_ID,
    SDI_REVIEW_TASK_ID,
    RDI_REVIEW_STATUS_ID,
    RDI_REVIEW_TASK_ID
} from './constants';

// Status id -> the admin "View" task injected when the engine leaves a step
// task-less (all-external outgoing edges). Add a row for the next one.
const STATUS_TASK_OVERRIDES: Readonly<Record<string, Readonly<StepTask>>> = {
    [PENDING_APPROVAL_STATUS_ID]: { id: APPROVAL_REVIEW_TASK_ID, label: 'View' },
    [SDI_REVIEW_STATUS_ID]: { id: SDI_REVIEW_TASK_ID, label: 'View' },
    [RDI_REVIEW_STATUS_ID]: { id: RDI_REVIEW_TASK_ID, label: 'View' }
};

// Backend tasks re-keyed to a frontend screen: Redesign runs the Design loop's screens, and
// keyed by status so post-approval keeps its own waiting-on-manufacturer screen.
const STATUS_TASK_ID_REKEYS: Readonly<Record<string, string>> = {
    [FINAL_REVIEW_STATUS_ID]: FINAL_REVIEW_TASK_ID,
    'Redesign - 1. Submit for redesign': 'submit-design-plan',
    'Redesign - 2. Waiting for redesign': 'wait-for-design',
    'Redesign - 3. Review redesign': 'review-design-finalize-dx',
    'Redesign - 5. Review Dx': FINAL_REVIEW_TASK_ID
};

// Single injection point every StatusMeta consumer reads from.
function withTaskOverride(status: StatusMeta): StatusMeta {
    const rekey = STATUS_TASK_ID_REKEYS[status.id];
    if (rekey && status.task) return { ...status, task: { ...status.task, id: rekey } };
    const override = STATUS_TASK_OVERRIDES[status.id];
    if (!override || status.task) return status;
    return { ...status, task: override };
}

// A renamed/removed status id silently kills its override (no task ever gets
// injected) - report each miss once so a backend rename doesn't go unnoticed.
const reportedMissingOverrideIds = new Set<string>();

function warnOnMissingOverrideStatuses(knownStatusIds: ReadonlySet<string>): void {
    const overrides: Record<string, string> = {
        ...Object.fromEntries(Object.entries(STATUS_TASK_OVERRIDES).map(([s, t]) => [s, t.id])),
        ...STATUS_TASK_ID_REKEYS
    };
    for (const [statusId, taskId] of Object.entries(overrides)) {
        if (knownStatusIds.has(statusId) || reportedMissingOverrideIds.has(statusId)) continue;
        reportedMissingOverrideIds.add(statusId);
        reportError({
            source: 'work-queue/util.buildStatusLookup',
            message: `STATUS_TASK_OVERRIDES references a status id absent from workflow meta: ${statusId}`,
            url: globalThis.location?.href ?? '',
            userAgent: navigator.userAgent,
            errorType: 'workflow_action_failure',
            metadata: { statusId, taskId }
        });
    }
}

export function buildStatusLookup(meta: WorkQueueMeta | undefined): Map<string, StatusMeta> {
    if (!meta) return new Map();
    warnOnMissingOverrideStatuses(new Set(meta.statuses.map((s) => s.id)));
    return new Map(meta.statuses.map((s) => [s.id, withTaskOverride(s)]));
}

export function invalidateWorkQueue(queryClient: QueryClient): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: ['work-queue'] });
}

// Per-case detail queries invalidated alongside the queue list. Every
// status-changing global action should call invalidateCaseDetail, not this list directly.
const CASE_DETAIL_QUERY_KEYS = [
    'patient-timeline',
    'patient-journey',
    'patient-card',
    'design-plan-history'
] as const;

export function invalidateCaseDetail(queryClient: QueryClient, caseId: string): Promise<unknown> {
    return Promise.all([
        invalidateWorkQueue(queryClient),
        ...CASE_DETAIL_QUERY_KEYS.map((key) =>
            queryClient.invalidateQueries({ queryKey: [key, caseId] })
        ),
        invalidateClarificationHistory(queryClient, caseId),
        queryClient.invalidateQueries({ queryKey: FINAL_REVIEW_HAND_BACK_KEY(caseId) }),
        // Hand-backs and approve change the Dx draft and its review history;
        // old rows may still be pending_review.
        queryClient.invalidateQueries({ queryKey: DX_REPORT_DRAFTS_KEY(caseId) }),
        invalidateSidebarCounts(queryClient)
    ]);
}

// Raw manufacturer portal statuses are UPPER_SNAKE; display them sentence-cased.
// Shared by the pill and the filter dropdown — filtering sends the RAW value.
export function humanizeManufacturerStatus(status: string): string {
    const words = status.replace(/_/g, ' ').trim().toLowerCase();
    if (!words) return status;
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// phaseTint moved to src/utils/phaseColors.ts, alongside PHASE_HEX.

export function practiceDoctorLabel(c: Pick<WorkQueueCase, 'practice' | 'practiceDoctor'>): string {
    return formatPracticeDoctorLabel('doctor-practice', c.practice, c.practiceDoctor);
}
